import store from '@/store';
import { EventType, IncidentType } from '@/modules/Types';

/**
 * Get translated name for an incident or event type
 * @param incidentType - The incident_type or event_type string
 * @param isEvent - Whether this is an event type (true) or incident type (false)
 * @param language - Language code (en, he, ar). If not provided, uses current store language
 * @returns Translated name or original incident type if not found
 */
export function getIncidentTypeName(
  incidentType: string,
  isEvent: boolean = false,
  language?: string
): string {
  const state = (store.state as any);
  const lang = language || state.Global?.lang || 'en';
  
  const typesData: (EventType | IncidentType)[] = isEvent 
    ? state.Projects?.eventTypes || []
    : state.Projects?.incidentTypes || [];
  
  const typeKey = isEvent ? 'event_type' : 'incident_type';
  
  const typeItem = typesData.find((item: any) => item[typeKey] === incidentType);
  
  if (!typeItem || !typeItem.name) {
    return incidentType;
  }
  
  return typeItem.name[lang] || typeItem.name.en || incidentType;
}

/**
 * Get translated description for an incident or event type
 * @param incidentType - The incident_type or event_type string
 * @param isEvent - Whether this is an event type (true) or incident type (false)
 * @param language - Language code (en, he, ar). If not provided, uses current store language
 * @returns Translated description or empty string if not found
 */
export function getIncidentTypeDescription(
  incidentType: string,
  isEvent: boolean = false,
  language?: string
): string {
  const state = (store.state as any);
  const lang = language || state.Global?.lang || 'en';
  
  const typesData: (EventType | IncidentType)[] = isEvent 
    ? state.Projects?.eventTypes || []
    : state.Projects?.incidentTypes || [];
  
  const typeKey = isEvent ? 'event_type' : 'incident_type';
  
  const typeItem = typesData.find((item: any) => item[typeKey] === incidentType);
  
  if (!typeItem || !typeItem.description) {
    return '';
  }
  
  return typeItem.description[lang] || typeItem.description.en || '';
}

/**
 * Get full incident/event type data
 * @param incidentType - The incident_type or event_type string
 * @param isEvent - Whether this is an event type (true) or incident type (false)
 * @returns Full type data or null if not found
 */
export function getIncidentTypeData(
  incidentType: string,
  isEvent: boolean = false
): EventType | IncidentType | null {
  const state = (store.state as any);
  
  const typesData: (EventType | IncidentType)[] = isEvent 
    ? state.Projects?.eventTypes || []
    : state.Projects?.incidentTypes || [];
  
  const typeKey = isEvent ? 'event_type' : 'incident_type';
  
  return typesData.find((item: any) => item[typeKey] === incidentType) || null;
}
